(ns app.front.ui.side-panel
  "Wrapper for different global side panel components"
  (:require [app.front.file.upload :as upload]
            [app.front.ui.viewport-observer :as viewport-observer]
            [app.front.user-settings :as user-settings]
            [core.front.carbon :as cc]
            [core.front.event :refer [<sub >evt]]
            [core.front.media-query :as media-query]
            [core.front.portal :as portal]
            [potpuri.core :as p]
            [reagent.core :as r]
            [schema.core :as s]))

(defn title [text]
  [:h2.font-bold.text-ui-night-sky-100.text-sm.leading-none.mt-2 text])

(def button-classes "w-full bg-gray-200 rounded-md !p-2 border-0 appearance-none !text-ui-night-sky-100 flex hover:!bg-ui-blue-10 focus:!bg-ui-blue-10 !min-h-0 !text-base !font-normal !normal-case !no-underline text-left disabled:!text-ui-night-sky-70 disabled:!cursor-not-allowed")

(defn button [{:keys [disabled? on-click href file-picker-opts loading? loading-description]} text]
  (let [button-tag (if (some? href) :a :button)]
    (cond
      ;; Loading indicator:
      loading?
      [button-tag
       {:class         [button-classes "!py-0.5 !bg-ui-blue-10 items-center"]
        :disabled      true
        :aria-disabled true
        :aria-busy     true}
       [:span (cc/inline-loading {})]
       [:span.flex-1 loading-description]]
      ;; File upload button:
      file-picker-opts
      [upload/file-picker-button
       (merge (select-keys file-picker-opts [:id :accept-types])
              {:tr           (<sub [:tr])
               :class        "w-full"
               :button-opts  {:kind       :ghost
                              :disabled   (boolean disabled?)
                              :class-name button-classes}
               :on-select-fn on-click})
       text]
      ;; Regular button:
      :else
      [button-tag
       (p/assoc-if {:disabled      (boolean disabled?)
                    :aria-disabled (boolean disabled?)
                    :class         button-classes}
                   :href href
                   :on-click on-click)
       text])))

(def ^:private min-width 320)
(def ^:private max-width 640)

(defn- clamp-width [w]
  (-> w (max min-width) (min max-width)))

(defn- load-panel-width [panel-id]
  (-> (or (when panel-id
            (user-settings/get-value [:side-panel-width panel-id] s/Int))
          min-width)
      clamp-width))

(defn- save-panel-width! [panel-id width]
  (when panel-id
    (>evt [::user-settings/save-user-settings [:side-panel-width panel-id] width s/Int])))

(defn- resize-drag-zone
  "Renders an invisible drag handle at the left edge of a panel and handles
  mouse-based horizontal resizing. Updates `*state` atom with :width and
  :dragging? keys. Persists final width to user-settings under `panel-id`."
  [*state panel-id]
  (let [on-mouse-down (fn [e]
                        (.preventDefault e)
                        (let [start-x (.-clientX e)
                              start-w (:width @*state)
                              on-move (fn [me]
                                        (let [dx    (- start-x (.-clientX me))
                                              new-w (clamp-width (+ start-w dx))]
                                          (swap! *state assoc :width new-w)))
                              on-up   (fn on-up-fn [_]
                                        (js/document.removeEventListener "mousemove" on-move)
                                        (js/document.removeEventListener "mouseup" on-up-fn)
                                        (set! (.. js/document -body -style -cursor) "")
                                        (set! (.. js/document -body -style -userSelect) "")
                                        (swap! *state assoc :dragging? false)
                                        (save-panel-width! panel-id (:width @*state)))]
                          (swap! *state assoc :dragging? true)
                          (set! (.. js/document -body -style -cursor) "col-resize")
                          (set! (.. js/document -body -style -userSelect) "none")
                          (js/document.addEventListener "mousemove" on-move)
                          (js/document.addEventListener "mouseup" on-up)))]
    [:div.absolute.top-0.bottom-0.z-10
     {:style         {:width  "16px"
                      :left   "-8px"
                      :cursor "col-resize"}
      :on-mouse-down on-mouse-down}]))

(defn side-panel [{:keys [panel-id panel-open?]} & children]
  (r/with-let [*state (r/atom {:width     (load-panel-width panel-id)
                               :dragging? false})]
    (let [desktop?                  (<sub [::media-query/breakpoint-matches? :lg])
          header-bottom             (<sub [::viewport-observer/visible-bottom :sticky-header])
          viewport-h                (<sub [::viewport-observer/viewport-height])
          height                    (max 0 (- viewport-h header-bottom))
          {:keys [width dragging?]} @*state]
      (if-not desktop?
        (when panel-open? (portal/render-in-portal (into [:<>] children) "mobile-side-panel-content"))
        [:div.bg-ui-white.overflow-x-clip.relative.hidden.lg:flex
         (merge {:class [(when-not dragging? "transition-all")
                         (when-not panel-open? "w-0 h-0 overflow-y-hidden")]
                 :style (when panel-open? {:width (str width "px")})}
                (when-not panel-open?
                  {:inert       ""
                   :aria-hidden true}))
         (when panel-open?
           [resize-drag-zone *state panel-id])
         (into [:div.p-4.box-border.flex.flex-col.gap-2.items-stretch.sticky.bottom-0.overflow-y-auto.overscroll-contain.shrink-0
                {:style {:height    (str height "px")
                         :top       (str header-bottom "px")
                         :width     (str width "px")
                         :min-width (str min-width "px")}}]
               (when panel-open? children))]))))
